import { isArray } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';

import {
  createLessonIntoBlock,
  deleteLessonIntoBlock,
  updateLessonIntoBlock,
} from '@/data/services/lessonBlocksServices';
import Lesson, { TempLesson } from '@/models/Lesson';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import JsonApiResponse from '@/models/JsonApiResponse';
import { LessonDTO } from '@/data/services/lessonServices';
import { adapterLessonToLessonDTO } from '@/utils/lesson/adapter';

type LessonOperation = {
  type: 'create' | 'update' | 'delete' | 'move';
  lesson: TempLesson;
  sourceBlockId?: number;
  targetBlockId?: number;
  originalLesson?: TempLesson;
};

type LessonOperationsResult = {
  createdLessons: TempLesson[];
  updatedLessons: TempLesson[];
  deletedLessons: TempLesson[];
  movedLessons: TempLesson[];
};

type LessonWithBlockId = {
  lesson: TempLesson;
  blockId: number;
};

type LessonOperations = {
  lesson: TempLesson;
  operations: LessonOperation[];
};

export function getPreviousBlocksLessons(
  currentBlockIndex: number,
  blocks: TempLessonBlock[],
) {
  let totalLessons = 0;

  for (let blockIndex = 0; blockIndex < currentBlockIndex; blockIndex++) {
    const blockLessons = blocks[blockIndex]?.lessons || [];
    const lessonCount = isArray(blockLessons) ? blockLessons.length : 0;
    totalLessons += lessonCount;
  }

  return totalLessons;
}

function buildCurrentLessonsMap(
  currentBlocks: TempLessonBlock[],
): Map<number, LessonWithBlockId> {
  const currentLessonsMap = new Map<number, LessonWithBlockId>();

  currentBlocks.forEach(block => {
    block.lessons.forEach(lesson => {
      if (lesson.id) {
        currentLessonsMap.set(lesson.id, { lesson, blockId: block.id });
      }
    });
  });

  return currentLessonsMap;
}

function buildNewLessonsMaps(newBlocks: TempLessonBlock[]): {
  newLessonsMap: Map<number, LessonWithBlockId>;
  newLessonsWithoutId: LessonWithBlockId[];
} {
  const newLessonsMap = new Map<number, LessonWithBlockId>();
  const newLessonsWithoutId: LessonWithBlockId[] = [];

  newBlocks.forEach(block => {
    block.lessons.forEach(lesson => {
      if (lesson.id) {
        newLessonsMap.set(lesson.id, { lesson, blockId: block.id });
      } else {
        newLessonsWithoutId.push({ lesson, blockId: block.id });
      }
    });
  });

  return { newLessonsMap, newLessonsWithoutId };
}

function detectDeletedLessons(
  currentLessonsMap: Map<number, LessonWithBlockId>,
  newLessonsMap: Map<number, LessonWithBlockId>,
  newBlocks: TempLessonBlock[],
): Map<number, LessonOperations> {
  const lessonOperationsMap = new Map<number, LessonOperations>();

  const existingBlockIds = new Set(newBlocks.map(block => block.id));

  currentLessonsMap.forEach(({ lesson, blockId }, lessonId) => {
    if (!newLessonsMap.has(lessonId) && existingBlockIds.has(blockId)) {
      lessonOperationsMap.set(lessonId, {
        lesson,
        operations: [
          {
            type: 'delete',
            lesson,
            sourceBlockId: blockId,
          },
        ],
      });
    }
  });

  return lessonOperationsMap;
}

function detectCreatedLessons(
  newLessonsWithoutId: LessonWithBlockId[],
): Map<string, LessonOperations> {
  const tempIdOperationsMap = new Map<string, LessonOperations>();

  newLessonsWithoutId.forEach(({ lesson, blockId }) => {
    if (lesson.tempId) {
      tempIdOperationsMap.set(lesson.tempId, {
        lesson,
        operations: [
          {
            type: 'create',
            lesson,
            targetBlockId: blockId,
          },
        ],
      });
    }
  });

  return tempIdOperationsMap;
}

function detectUpdatedAndMovedLessons(
  currentLessonsMap: Map<number, LessonWithBlockId>,
  newLessonsMap: Map<number, LessonWithBlockId>,
): Map<number, LessonOperations> {
  const lessonOperationsMap = new Map<number, LessonOperations>();

  newLessonsMap.forEach(({ lesson, blockId }, lessonId) => {
    const currentLessonData = currentLessonsMap.get(lessonId);
    if (currentLessonData) {
      const { lesson: currentLesson, blockId: currentBlockId } =
        currentLessonData;
      const operations: LessonOperation[] = [];

      if (currentBlockId !== blockId) {
        operations.push({
          type: 'move',
          lesson,
          sourceBlockId: currentBlockId,
          targetBlockId: blockId,
          originalLesson: currentLesson,
        });
      }

      const { order, ...lessonWithoutOrder } = lesson;
      const changes = buildChangedObject(currentLesson, lessonWithoutOrder);

      if (Object.keys(changes).length > 0) {
        operations.push({
          type: 'update',
          lesson,
          sourceBlockId: currentBlockId,
          targetBlockId: blockId,
          originalLesson: currentLesson,
        });
      }

      if (operations.length > 0) {
        lessonOperationsMap.set(lessonId, {
          lesson,
          operations,
        });
      }
    }
  });

  return lessonOperationsMap;
}

function detectLessonOperations(
  currentBlocks: TempLessonBlock[],
  newBlocks: TempLessonBlock[],
): LessonOperations[] {
  const currentLessonsMap = buildCurrentLessonsMap(currentBlocks);
  const { newLessonsMap, newLessonsWithoutId } = buildNewLessonsMaps(newBlocks);

  const deletedLessonsMap = detectDeletedLessons(
    currentLessonsMap,
    newLessonsMap,
    newBlocks,
  );
  const createdLessonsMap = detectCreatedLessons(newLessonsWithoutId);
  const updatedAndMovedLessonsMap = detectUpdatedAndMovedLessons(
    currentLessonsMap,
    newLessonsMap,
  );

  const allOperations = [
    ...deletedLessonsMap.values(),
    ...createdLessonsMap.values(),
    ...updatedAndMovedLessonsMap.values(),
  ];

  return allOperations;
}

function groupLessonOperations(lessonOperations: LessonOperations[]) {
  const allOperations: LessonOperation[] = [];

  lessonOperations.forEach(({ operations }) => {
    allOperations.push(...operations);
  });

  return {
    create: allOperations.filter(op => op.type === 'create'),
    update: allOperations.filter(op => op.type === 'update'),
    delete: allOperations.filter(op => op.type === 'delete'),
    move: allOperations.filter(op => op.type === 'move'),
  };
}

async function executeLessonOperations(
  slug: string,
  lessonOperations: LessonOperations[],
): Promise<LessonOperationsResult> {
  const grouped = groupLessonOperations(lessonOperations);

  const result: LessonOperationsResult = {
    createdLessons: [],
    updatedLessons: [],
    deletedLessons: [],
    movedLessons: [],
  };

  for (const operation of grouped.create) {
    if (operation.targetBlockId) {
      const { tempId, ...lesson } = operation.lesson;

      const createdLesson = await createLessonIntoBlock(
        slug,
        operation.targetBlockId,
        adapterLessonToLessonDTO(lesson) as LessonDTO,
      );

      result.createdLessons.push({
        ...createdLesson,
        tempId: operation.lesson.tempId,
        chapters: operation.lesson.chapters,
      });
    }
  }

  for (const operation of grouped.update) {
    if (!operation.originalLesson) continue;

    const changes = buildChangedObject(
      operation.originalLesson,
      operation.lesson,
      {
        ignoreFields: ['tempId', 'chapters', 'book'],
      },
    );

    if (Object.keys(changes).length === 0) continue;

    const blockId = operation.sourceBlockId || operation.targetBlockId;
    if (!blockId) continue;

    const updatedLesson = await updateLessonIntoBlock(
      slug,
      blockId,
      operation.lesson.id,
      changes as Partial<LessonDTO>,
    );
    result.updatedLessons.push({
      ...updatedLesson,
      chapters: operation.lesson.chapters,
    });
  }

  for (const operation of grouped.delete) {
    if (operation.sourceBlockId) {
      await deleteLessonIntoBlock(
        slug,
        operation.sourceBlockId,
        operation.lesson.id,
      );
      result.deletedLessons.push(operation.lesson);
    }
  }

  for (const operation of grouped.move) {
    if (operation.sourceBlockId && operation.targetBlockId) {
      result.movedLessons.push(operation.lesson);
    }
  }

  return result;
}

function buildLessonMaps(operationsResult: LessonOperationsResult) {
  const lessonMap = new Map<number, TempLesson>();
  const tempIdMap = new Map<string, TempLesson>();

  const lessons = [
    ...operationsResult.createdLessons,
    ...operationsResult.movedLessons,
    ...operationsResult.updatedLessons,
  ];

  lessons.forEach(lesson => {
    lessonMap.set(lesson.id, lesson);
    if (lesson.tempId) {
      tempIdMap.set(lesson.tempId, lesson);
    }
  });

  return { lessonMap, tempIdMap };
}

function updateLessonInBlock(
  lesson: TempLesson,
  lessonMap: Map<number, TempLesson>,
  tempIdMap: Map<string, TempLesson>,
) {
  if (lesson.id && lessonMap.has(lesson.id)) {
    return lessonMap.get(lesson.id)!;
  }
  if (lesson.tempId && tempIdMap.has(lesson.tempId)) {
    return tempIdMap.get(lesson.tempId)!;
  }
  return lesson;
}

function rebuildBlocks(
  newBlocks: TempLessonBlock[],
  operationsResult: LessonOperationsResult,
): TempLessonBlock[] {
  const { lessonMap, tempIdMap } = buildLessonMaps(operationsResult);

  const updatedBlocks = newBlocks.map(block => ({
    ...block,
    lessons: block.lessons.map(lesson =>
      updateLessonInBlock(lesson, lessonMap, tempIdMap),
    ),
  }));

  return updatedBlocks;
}

async function performCreateLessons(
  slug: string,
  blockId: number,
  lessons: TempLesson[],
) {
  const createdLessons: Lesson[] = [];

  for (const lesson of lessons) {
    const created = await createLessonIntoBlock(
      slug,
      blockId,
      adapterLessonToLessonDTO(lesson) as LessonDTO,
    );
    createdLessons.push(created);
  }

  return createdLessons;
}

export function useCourseStudioLessons() {
  const queryClient = useQueryClient();

  function updateLessonsIntoBlocks(
    slug: string,
    blockId: number,
    lessons: TempLesson[],
  ) {
    const queryKey = lessonBlocksQueryKeys.listLessons(slug, blockId).queryKey;
    const data = queryClient.getQueryData<JsonApiResponse<Lesson>>(queryKey);

    if (!data) return;

    queryClient.setQueryData<JsonApiResponse<Lesson>>(queryKey, {
      ...data,
      results: lessons as Lesson[],
    });
  }

  async function handleLessonOperations(
    slug: string,
    currentBlocks: TempLessonBlock[],
    newBlocks: TempLessonBlock[],
  ): Promise<TempLessonBlock[]> {
    const operations = detectLessonOperations(currentBlocks, newBlocks);

    let finalBlocks = newBlocks;

    const operationsResult = await executeLessonOperations(slug, operations);

    finalBlocks = rebuildBlocks(newBlocks, operationsResult);

    finalBlocks.forEach(block => {
      updateLessonsIntoBlocks(slug, block.id, block.lessons);
    });

    return finalBlocks;
  }

  async function handleCreateLessons(
    slug: string,
    blockId: number,
    lessons: TempLesson[],
  ) {
    const createdLessons = await performCreateLessons(slug, blockId, lessons);

    return createdLessons;
  }

  return {
    handleCreateLessons,
    handleLessonOperations,
  };
}
